import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { logError } from "@/lib/logger";

const ContentContext = createContext(null);
const EDIT_FLAG = "live_edit";

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [content, setContent] = useState({});
  const [pending, setPending] = useState({});
  const [editFlag, setEditFlag] = useState(() => sessionStorage.getItem(EDIT_FLAG) === "1");
  const [preview, setPreview] = useState(false);
  const liveActive = editFlag && isAuthenticated;
  const editMode = liveActive && !preview;
  const previewMode = liveActive && preview;

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/content`);
      setContent(res.data || {});
    } catch (error) {
      logError("Failed to load site content:", error);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const get = useCallback((key) => pending[key] ?? content[key], [pending, content]);

  const text = useCallback((key, fallback = "") => {
    const entry = pending[key] ?? content[key];
    return entry && entry.content !== "" && entry.content != null ? entry.content : fallback;
  }, [pending, content]);

  const stage = useCallback((key, value) => {
    setPending((prev) => ({ ...prev, [key]: value }));
  }, []);

  const enterEditMode = useCallback(() => {
    sessionStorage.setItem(EDIT_FLAG, "1");
    setEditFlag(true);
  }, []);

  const exitEditMode = useCallback(() => {
    sessionStorage.removeItem(EDIT_FLAG);
    setEditFlag(false);
    setPreview(false);
    setPending({});
  }, []);

  const discard = useCallback(() => setPending({}), []);

  const publish = useCallback(async () => {
    const keys = Object.keys(pending);
    if (!keys.length) return true;
    try {
      await axios.put(`${API}/admin/content`, { sections: pending }, { headers: { Authorization: `Bearer ${token}` } });
      setContent((prev) => ({ ...prev, ...pending }));
      setPending({});
      toast.success(`Published ${keys.length} change${keys.length === 1 ? "" : "s"}`);
      return true;
    } catch (error) {
      toast.error("Failed to publish changes");
      return false;
    }
  }, [pending, token]);

  const value = useMemo(() => ({
    content, pending, pendingCount: Object.keys(pending).length,
    get, text, stage, refresh,
    editMode, liveActive, previewMode, setPreview, enterEditMode, exitEditMode, discard, publish,
  }), [content, pending, get, text, stage, refresh, editMode, liveActive, previewMode, enterEditMode, exitEditMode, discard, publish]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const resolveUrl = (url) =>
  url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
